import knowledgeBase from '../ai_knowledge/aziz_edu_knowledge_base.json';

export interface Program {
  id: string;
  name: string;
  category: string;
  duration: string;
  creditHours: string;
  description: string;
  coordinator: string;
  benefits: string[];
  admissionCriteria: string;
  careerProspects: string;
}

export interface Institution {
  name: string;
  fullOfficialName: string;
  recognition: string;
  charter: string;
  yearOfEstablishment: number;
  campusAddress: string;
  department: string;
  officeTimings: string;
  contactInfo: string;
}

export interface FeeStructure {
  admissionFee: string;
  tuitionPerSemester: string;
  facilitationFee: string;
  securityDeposit: string;
  examFee: string;
  admissionProcessingFee: string;
  hostelFees: {
    sharingRoom: string;
    separateRoom: string;
  };
  transportFees: {
    gulshanEcadeed: string;
    city: string;
  };
  refundPolicy: {
    fullRefund: string;
    halfRefund: string;
    noRefund: string;
  };
}

export interface AdmissionProcess {
  steps: string[];
  requiredDocuments: string[];
  selectionProcess: string[];
}

export interface AdmissionCalendar {
  admissionOpening: string;
  lastDateToApply: string;
  entryTestDates: string;
  interviewSchedule: string;
  meritListDates: string;
  feeDeadline: string;
  semesterStartDate: string;
}

export interface PlacementData {
  topHiringCompanies: string[];
  placementRatio: string;
  internshipPartners: string[];
  successStories: string[];
  industryMous: string[];
}

export interface CampusFacilities {
  academic: string[];
  studentServices: string[];
  activities: string[];
}

export interface LeadCaptureFields {
  mandatory: string[];
  optional: string[];
}

export interface AiBehaviorRules {
  tone: string[];
  safety: string[];
  escalation: string[];
}

export interface KnowledgeBase {
  institution: Institution;
  programs: Program[];
  feeStructure: FeeStructure;
  scholarships: string[];
  admissionProcess: AdmissionProcess;
  admissionCalendar: AdmissionCalendar;
  campusFacilities: CampusFacilities;
  placementData: PlacementData;
  leadCaptureFields: LeadCaptureFields;
  aiBehaviorRules: AiBehaviorRules;
}

class KnowledgeService {
  private knowledge: KnowledgeBase;

  constructor() {
    this.knowledge = knowledgeBase as KnowledgeBase;
  }

  getInstitutionInfo(): Institution {
    return this.knowledge.institution;
  }

  getPrograms(): Program[] {
    return this.knowledge.programs;
  }

  getProgramById(id: string): Program | undefined {
    return this.knowledge.programs.find(program => program.id === id);
  }

  getProgramByName(name: string): Program | undefined {
    return this.knowledge.programs.find(program => 
      program.name.toLowerCase().includes(name.toLowerCase())
    );
  }

  getProgramsByCategory(category: string): Program[] {
    return this.knowledge.programs.filter(program => 
      program.category.toLowerCase().includes(category.toLowerCase())
    );
  }

  getFeeStructure(): FeeStructure {
    return this.knowledge.feeStructure;
  }

  getAdmissionProcess(): AdmissionProcess {
    return this.knowledge.admissionProcess;
  }

  getAdmissionCalendar(): AdmissionCalendar {
    return this.knowledge.admissionCalendar;
  }

  getCampusFacilities(): CampusFacilities {
    return this.knowledge.campusFacilities;
  }

  getPlacementData(): PlacementData {
    return this.knowledge.placementData;
  }

  getScholarships(): string[] {
    return this.knowledge.scholarships;
  }

  searchPrograms(query: string): Program[] {
    const lowerQuery = query.toLowerCase();
    return this.knowledge.programs.filter(program => 
      program.name.toLowerCase().includes(lowerQuery) ||
      program.category.toLowerCase().includes(lowerQuery) ||
      program.description.toLowerCase().includes(lowerQuery) ||
      program.benefits.some(benefit => benefit.toLowerCase().includes(lowerQuery))
    );
  }
}

export const knowledgeService = new KnowledgeService();